import Dexie, { Table } from 'dexie';
import { TimeEntry, Project, UserInfo, PendingVoiceCommand, TimesheetSubmission, ShoppingItem, TodoItem, PendingReceipt } from '../models/types';

/**
 * The single definition of "this entry still owes the dashboard a copy":
 * a closed shift that has either never been delivered, or has been edited
 * since it last was.
 *
 * Exported and shared on purpose, and it must stay the ONLY copy of this rule.
 * getUnsyncedEntries() below is what autoSync and the enrollment handshake
 * select with, but syncTimeEntries() has to apply the same test to whatever
 * array it is handed — WeeklyTimesheet passes the entire displayed week,
 * already-synced rows included. A second, hand-rolled copy of the predicate
 * would drift, and a drift here re-sends reconciled history under the enrolled
 * employee id, which the dashboard cannot recognize and inserts as a duplicate.
 */
export const isEntryUnsynced = (entry: TimeEntry): boolean => {
  // A tombstone must reach the dashboard even for a shift that was never
  // closed. An entry that never synced has no server row to remove, so
  // EditEntryModal deletes those outright and they never appear here.
  if (entry.deletedAt) return Boolean(entry.syncedAt);
  if (!entry.timeOut) return false; // an open shift isn't ready to send
  if (!entry.syncedAt) return true;
  return new Date(entry.lastModified).getTime() > new Date(entry.syncedAt).getTime();
};

class AppDatabase extends Dexie {
  timeEntries!: Table<TimeEntry>;
  projects!: Table<Project>;
  userInfo!: Table<UserInfo>;
  pendingVoiceCommands!: Table<PendingVoiceCommand>;
  timesheetSubmissions!: Table<TimesheetSubmission>;
  shoppingItems!: Table<ShoppingItem>;
  todoItems!: Table<TodoItem>;
  pendingReceipts!: Table<PendingReceipt>;

  constructor() {
    // Use a new database name to avoid conflicts with any existing database
    super('DayTrakrLiteDB_v4');
    
    this.version(1).stores({
      timeEntries: '++id, timeIn, timeOut, project, task, location',
      projects: '++id, name',
      userInfo: '++id, name'
    });
    
    // Add pendingVoiceCommands table in version 2
    this.version(2).stores({
      pendingVoiceCommands: '++id, timestamp, processed'
    });
    
    // Add verified flag to timeEntries and timesheetSubmissions table in version 3
    this.version(3).stores({
      timeEntries: '++id, timeIn, timeOut, project, task, location, verified',
      timesheetSubmissions: '++id, weekStartDate, weekEndDate, submissionDate, submittedBy, status'
    });
    
    // Add shopping list and to-do list tables in version 4
    this.version(4).stores({
      shoppingItems: '++id, text, completed, createdAt',
      todoItems: '++id, text, completed, createdAt'
    });
    
    // Add entryId and lastModified to timeEntries in version 5
    this.version(5).stores({
      timeEntries: '++id, entryId, timeIn, timeOut, project, task, location, verified, lastModified',
      appState: 'id, lastEntryId'
    }).upgrade(tx => {
      // Migration: Add lastModified field and initialize entryId for existing entries
      return tx.table('timeEntries').toCollection().modify(entry => {
        entry.lastModified = entry.timeOut || entry.timeIn;
        // entryId will be set when the entry is next updated
      });
    });
    
    // Create appState table if it doesn't exist
    this.version(5).stores({
      appState: 'id, lastEntryId'
    });
    
    // Add email to userInfo in version 6
    this.version(6).stores({
      userInfo: '++id, name, email'
    }).upgrade(tx => {
      return tx.table('userInfo').toCollection().modify(user => {
        user.email = user.email || '';
      });
    });

    // v7 — receipt capture (Phase 2).
    //
    // `projects` is re-declared to add the remoteId index: the dashboard's own
    // projects.id, which syncProjectsFromDashboard used to discard. Without it
    // a receipt can only name its job, never identify it.
    //
    // needsAttention is indexed as 0/1 because Dexie cannot index booleans.
    this.version(7).stores({
      pendingReceipts: '++id, clientUuid, createdAt, needsAttention',
      projects: '++id, name, remoteId',
    });
  }

  async getCurrentTimeEntry(): Promise<TimeEntry | undefined> {
    return this.timeEntries
      .filter(entry => !entry.timeOut)
      .last();
  }

  async getLastTimeEntry(): Promise<TimeEntry | undefined> {
    return this.timeEntries
      .orderBy('timeIn')
      .last();
  }

  /**
   * A deleted entry is gone as far as the employee is concerned. The row
   * survives only as a tombstone so the deletion can reach the dashboard on
   * the next sync — showing it back on screen would look like the delete
   * failed, and the obvious response (delete it again) re-stamps the tombstone
   * and re-sends it every sync from then on.
   *
   * This must NOT be applied to what gets handed to syncTimeEntries: the
   * tombstones are precisely what that call has to deliver.
   */
  private static readonly notDeleted = (entry: TimeEntry) => !entry.deletedAt;

  async getTimeEntriesForDay(date: Date): Promise<TimeEntry[]> {
    const startOfDay = new Date(date);
    startOfDay.setHours(0, 0, 0, 0);

    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 59, 999);

    return this.timeEntries
      .where('timeIn')
      .between(startOfDay, endOfDay, true, true)
      .and(AppDatabase.notDeleted)
      .toArray();
  }

  async getTimeEntriesForWeek(date: Date): Promise<TimeEntry[]> {
    const day = date.getDay();
    const startOfWeek = new Date(date);
    startOfWeek.setDate(date.getDate() - day);
    startOfWeek.setHours(0, 0, 0, 0);
    
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);
    
    return this.timeEntries
      .where('timeIn')
      .between(startOfWeek, endOfWeek, true, true)
      .and(AppDatabase.notDeleted)
      .toArray();
  }

  async getUserName(): Promise<string | null> {
    const userInfo = await this.userInfo.toArray();
    if (userInfo.length > 0) {
      return userInfo[0].name;
    }
    return null;
  }
  
  async getUserEmail(): Promise<string | null> {
    const userInfo = await this.userInfo.toArray();
    if (userInfo.length > 0) {
      return userInfo[0].email || null;
    }
    return null;
  }

  async setUserInfo(name: string, email?: string): Promise<void> {
    const existingUser = await this.userInfo.toArray();
    const updates: any = {
      name,
      lastModified: new Date()
    };
    
    if (email !== undefined) {
      updates.email = email;
    }
    
    if (existingUser.length > 0) {
      await this.userInfo.update(existingUser[0].id!, updates);
    } else {
      await this.userInfo.add({
        name,
        email: email || '',
        lastModified: new Date()
      });
    }
  }

  // Deprecated: Use setUserInfo instead
  async setUserName(name: string): Promise<void> {
    return this.setUserInfo(name);
  }

  async getEnrollmentCode(): Promise<string | null> {
    const rows = await this.userInfo.toArray();
    return rows[0]?.enrollmentCode || null;
  }

  async setEnrollmentCode(code: string): Promise<void> {
    const rows = await this.userInfo.toArray();
    if (rows.length > 0) {
      await this.userInfo.update(rows[0].id!, {
        enrollmentCode: code,
        lastModified: new Date(),
      });
    } else {
      // A device can enroll before it has a name on file; don't lose the code.
      await this.userInfo.add({
        name: '',
        enrollmentCode: code,
        lastModified: new Date(),
      } as any);
    }
  }

  async clearEnrollmentCode(): Promise<void> {
    const rows = await this.userInfo.toArray();
    if (rows.length > 0) {
      await this.userInfo.update(rows[0].id!, {
        enrollmentCode: undefined,
        lastModified: new Date(),
      });
    }
  }

  /**
   * Completed shifts whose latest edit has not reached the dashboard.
   * Filtered in memory: syncedAt is deliberately not indexed (no Dexie version
   * bump, no migration on devices), and entry counts here are in the hundreds.
   */
  async getUnsyncedEntries(): Promise<TimeEntry[]> {
    const all = await this.timeEntries.toArray();
    return all.filter(isEntryUnsynced);
  }

  private async getNextEntryId(): Promise<number> {
    // Get or create the app state
    let appState = await this.table('appState').get('entryIdCounter');
    let nextId = 1;
    
    if (appState && appState.lastEntryId) {
      nextId = (appState.lastEntryId % 999) + 1;
    }
    
    // Update the counter
    await this.table('appState').put({
      id: 'entryIdCounter',
      lastEntryId: nextId
    }, 'entryIdCounter');
    
    return nextId;
  }
  
  async addTimeEntry(entry: Omit<TimeEntry, 'id' | 'entryId' | 'lastModified' | 'createdAt'>): Promise<number> {
    const now = new Date();
    const entryId = await this.getNextEntryId();
    
    return this.timeEntries.add({
      ...entry,
      entryId,
      createdAt: now,
      lastModified: now,
      verified: false
    });
  }
  
  async updateTimeEntry(entryId: number, updates: Partial<Omit<TimeEntry, 'id' | 'entryId' | 'createdAt'>>): Promise<void> {
    await this.timeEntries.update(entryId, {
      ...updates,
      lastModified: new Date()
    });
  }
  
  async updateEntryVerification(entryId: number, verified: boolean): Promise<void> {
    await this.timeEntries.update(entryId, { 
      verified,
      lastModified: new Date() 
    });
  }
  
  async saveTimesheetSubmission(submission: TimesheetSubmission): Promise<number> {
    return await this.timesheetSubmissions.add(submission);
  }
  
  async getTimesheetSubmission(weekStartDate: Date): Promise<TimesheetSubmission | undefined> {
    const startOfDay = new Date(weekStartDate);
    startOfDay.setHours(0, 0, 0, 0);
    
    const endOfDay = new Date(weekStartDate);
    endOfDay.setHours(23, 59, 59, 999);
    
    return this.timesheetSubmissions
      .where('weekStartDate')
      .between(startOfDay, endOfDay, true, true)
      .first();
  }
  
  async getVerifiedTimeEntries(weekStartDate: Date, weekEndDate: Date): Promise<TimeEntry[]> {
    return this.timeEntries
      .where('timeIn')
      .between(weekStartDate, weekEndDate, true, true)
      .and(entry => entry.verified === true && !entry.deletedAt)
      .toArray();
  }
  
  // Shopping list methods
  async getShoppingItems(): Promise<ShoppingItem[]> {
    return this.shoppingItems
      .orderBy('createdAt')
      .toArray();
  }
  
  async addShoppingItem(text: string): Promise<number> {
    return await this.shoppingItems.add({
      text: text.trim(),
      completed: false,
      createdAt: new Date()
    });
  }
  
  async updateShoppingItem(id: number, changes: Partial<ShoppingItem>): Promise<void> {
    await this.shoppingItems.update(id, changes);
  }
  
  async deleteShoppingItem(id: number): Promise<void> {
    await this.shoppingItems.delete(id);
  }
  
  // Todo list methods
  async getTodoItems(): Promise<TodoItem[]> {
    return this.todoItems
      .orderBy('createdAt')
      .toArray();
  }
  
  async addTodoItem(text: string): Promise<number> {
    return await this.todoItems.add({
      text: text.trim(),
      completed: false,
      createdAt: new Date()
    });
  }
  
  async updateTodoItem(id: number, changes: Partial<TodoItem>): Promise<void> {
    await this.todoItems.update(id, changes);
  }
  
  async deleteTodoItem(id: number): Promise<void> {
    await this.todoItems.delete(id);
  }
  
  /**
   * Everything, tombstones included — deliberately unfiltered. This feeds
   * buildDatabaseBackupPayload, and a backup that quietly drops rows is not a
   * backup: restoring it would resurrect shifts the employee deleted, and
   * lose the pending deletions that had not reached the dashboard yet.
   * Callers that render or export FOR THE USER must drop `deletedAt` rows
   * themselves — see exportAllToCsv.
   */
  async getAllTimeEntries(): Promise<TimeEntry[]> {
    return this.timeEntries.toArray();
  }
}

export const db = new AppDatabase();
